import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import type { OfferItem } from '@/services/inventory-api.service';
import { offerCategoryMatchesOverride } from '@/utils/offer-category-match';

function getCustomerType(item: OfferItem): string {
  return typeof item.customer_type === 'string' ? item.customer_type.trim() : '';
}

function isDisplayPassItem(item: OfferItem): boolean {
  return item.type === 'pass' && getCustomerType(item).length > 0;
}

function offerLineKey(item: OfferItem): string {
  if (item.pass_pk != null) {
    return `pk:${String(item.pass_pk)}`;
  }
  return `ct:${getCustomerType(item).toLowerCase()}`;
}

function sortOfferItemsForDisplay(items: OfferItem[]): OfferItem[] {
  return [...items].sort((a, b) => {
    const aType = getCustomerType(a);
    const bType = getCustomerType(b);
    const aIsAdult = aType.toLowerCase() === 'adult' ? 0 : 1;
    const bIsAdult = bType.toLowerCase() === 'adult' ? 0 : 1;
    if (aIsAdult !== bIsAdult) return aIsAdult - bIsAdult;
    return aType.localeCompare(bType, undefined, { sensitivity: 'base' });
  });
}

/**
 * Merges supplemental offer lines (e.g. from trip skier counts only) into the primary
 * result (e.g. birthdate-based pricing). Primary wins when the same pass line exists in both.
 */
export function mergeOfferItemsForDisplay(primary: OfferItem[], supplemental: OfferItem[]): OfferItem[] {
  const primaryPassItems = primary.filter(isDisplayPassItem);
  const supplementalPassItems = supplemental.filter(isDisplayPassItem);
  if (supplementalPassItems.length === 0) return sortOfferItemsForDisplay(primaryPassItems);

  const map = new Map<string, OfferItem>();
  for (const item of primaryPassItems) {
    map.set(offerLineKey(item), item);
  }
  for (const item of supplementalPassItems) {
    const k = offerLineKey(item);
    if (!map.has(k)) {
      map.set(k, item);
    }
  }
  return sortOfferItemsForDisplay([...map.values()]);
}

export function mergeDomainOffersForDisplay(
  primary: DomainOffersEntry[],
  baseline: DomainOffersEntry[]
): DomainOffersEntry[] {
  if (primary.length === 0 && baseline.length === 0) return [];
  if (baseline.length === 0) return primary;

  const primaryByPk = new Map(primary.map((e) => [e.domain.pk, e]));
  const baselineByPk = new Map(baseline.map((e) => [e.domain.pk, e]));
  const orderPks: number[] = [];
  for (const e of primary) {
    if (!orderPks.includes(e.domain.pk)) orderPks.push(e.domain.pk);
  }
  for (const e of baseline) {
    if (!orderPks.includes(e.domain.pk)) orderPks.push(e.domain.pk);
  }

  return orderPks
    .map((pk) => {
      const p = primaryByPk.get(pk);
      const b = baselineByPk.get(pk);
      if (p && b) {
        return {
          ...p,
          offers: {
            ...p.offers,
            items: mergeOfferItemsForDisplay(p.offers.items, b.offers.items),
          },
        };
      }
      if (p) return p;
      return b ?? null;
    })
    .filter((e): e is DomainOffersEntry => e != null);
}

export function collectGlobalAgeCategoryLabels(
  entries: DomainOffersEntry[],
  passSelections: { ageCategory: string }[] | null | undefined
): string[] {
  const set = new Set<string>();
  for (const e of entries) {
    for (const item of e.offers.items) {
      if (item.type !== 'pass') continue;
      const t = item.customer_type.trim();
      if (t) set.add(t);
    }
  }
  if (passSelections?.length) {
    for (const o of passSelections) {
      const t = o.ageCategory.trim();
      if (t) set.add(t);
    }
  }
  return [...set];
}

export function domainOffersHasCategoryLabel(
  entry: DomainOffersEntry,
  categoryLabel: string
): boolean {
  return entry.offers.items.some((item) => {
    if (item.type !== 'pass') return false;
    return offerCategoryMatchesOverride(item.customer_type, categoryLabel)
  });
}
